<template>
  <Card
    class="cms-styles-adaption header"
    :footerDivider="false"
    :headerDivider="false"
    :titleFontsizeBig="false"
  >
    <template v-slot:content>
      <div>
        <p class="header-temp">{{ $t('creation.general.title') }}</p>
        <p class="description" v-dompurify-html="$t('creation.general.description2')"></p>
        <p v-if="showErrorResponse" class="error">
          <span v-dompurify-html="$t('creation.general.error')"></span>
          <span class="no-wrap">
            <button
              v-if="isTenantDA"
              @click="openInfoModal()"
              class="hereButton"
              tabindex="0"
            >
              {{ $t('creation.general.here-button') }}
            </button>
            <button
              v-if="isTenantDA"
              @click="openInfoModal()"
              class="icon-vorteile"
              tabindex="0"
              aria-label="Info-Button"
            ></button>
          </span>
        </p>
        <BasicTextfield
          @input="updateVsnrValue"
          id="creation.vsnr"
          :value="vsnrValue"
          :label="$t('creation.vsnr.label')"
          :status="vsnrTextfieldStatus"
          @keyup.enter="onSubmit"
          :lockableTrigger="true"
          :lockerLocked="true"
        >
          <template v-slot:error>
            {{ $t('creation.vsnr.error') }}
          </template>
          <template v-slot:info>
            <div v-dompurify-html="$t('creation.vsnr.info')"></div>
          </template>
        </BasicTextfield>
        <BasicTextfield
          @input="updateEmailValue"
          id="creation.email"
          type="email"
          :value="emailValue"
          :label="$t('creation.email.label')"
          :status="emailTextfieldStatus"
          @keyup.enter="onSubmit"
          :lockableTrigger="true"
          :lockerLocked="true"
        >
          <template v-slot:error>
            <div v-dompurify-html="$t('creation.email.error')"></div>
          </template>
          <template v-slot:info>
            {{ $t('creation.email.info') }}
          </template>
        </BasicTextfield>

        <BasicTextfield
          @input="updatePostcodeValue"
          id="creation.postcode"
          :value="postcodeValue"
          :label="$t('creation.postcode.label')"
          :status="postcodeTextfieldStatus"
          @keyup.enter="onSubmit"
          @update:input="notifyInputPostcode"
          :required="true"
        >
          <template v-slot:error>
            {{ $t('creation.postcode.error') }}
          </template>
          <template v-slot:info>
            <div v-dompurify-html="$t('creation.postcode.info')"></div>
          </template>
        </BasicTextfield>
        <DateInput
          v-model="birthdateValue"
          id="creation.birthdate"
          autocomplete="off"
          @keyup.enter="onSubmit"
          @update:invalid-date="notifyErrorDate"
          @update:input="notifyInputDate"
          :status="birthdateTextfieldStatus"
          :label="$t('creation.birthdate.label')"
          :placeholder="$t('creation.birthdate.placeholder')"
          mask="DD.MM.YYYY"
          :minValidYear="18"
          :required="true"
        >
          <template v-slot:error>
            <div v-dompurify-html="$t('creation.birthdate.error')"></div>
          </template>
          <template v-slot:info>
            <div v-dompurify-html="$t('creation.birthdate.info')"></div>
          </template>
        </DateInput>
      </div>
    </template>
    <template v-slot:footer>
      <div class="button-style">
        <CustomButton
          @click="onSubmit"
          buttonId="creation.registrieren-button"
          :buttonColor="ButtonColor.PRIMARY"
          :buttonSize="ButtonSize.BIG"
          :pendingState="pending"
          trackId="register"
          >{{ $t('creation.general.button-registration2') }}
        </CustomButton>
      </div>
    </template>
  </Card>
  <Modal
    ref="modalRef"
    :showModal="showInfoModal && isTenantDA"
    :title="$t('creation.infoModal.label')"
    @closeModal="closeInfoModal"
    :showCloseIcon="true"
    :max-width="700"
  >
    <template #content>
      <ImageWrapper
        class="imageWrapper"
        imagePath="assets/images/Weitere_Anliegen"
        imageName="Weitere_Anliegen"
        :breakpoints="[365, 450, 580, 639, 776]"
      ></ImageWrapper>
      <div class="modal-text">
        <p v-dompurify-html="$t('creation.infoModal.checkDetails')"></p>
        <div>
          <ul>
            <li v-dompurify-html="$t('creation.infoModal.checkInsuranceNumber')"></li>
            <li v-dompurify-html="$t('creation.infoModal.checkZipcode')"></li>
            <li v-dompurify-html="$t('creation.infoModal.checkBirthdate')"></li>
          </ul>
        </div>
        <p
          class="additionalHelp"
          v-dompurify-html="$t('creation.infoModal.additionalHelp')"
        ></p>
      </div>
    </template>
    <template #footer>
      <div class="footer-cta footer-cta--right">
        <CustomButton
          @click="closeInfoModal"
          id="registration-button"
          :buttonColor="ButtonColor.SECONDARY"
        >
          {{ $t('creation.infoModal.buttonRegistration') }}
        </CustomButton>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { navigate } from '@/utils/NavigationUtils'
import { escapeVSNR } from '@/utilsTenant/rules'
import {
  BasicTextfield,
  ButtonColor,
  ButtonSize,
  Card,
  CustomButton,
  DateInput,
  InputStatus,
  Modal
} from 'de-zurich-ui-library'

import {
  checkIfBirthdayInputIsValid,
  checkIfEmailInputIsValid,
  checkIfPostcodeInputIsValid,
  checkIfVsnrInputIsValid,
  is5xxError,
  isError400UserExisting
} from '@/components/account-management/accountcreation/accountcreationUtils'
import dayjs from 'dayjs'
import { onMounted, ref } from 'vue'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import ImageWrapper from '@/components/rahmen/ImageWrapper.vue'
import { isDa } from '@/utils/CommonUtils.ts'
import { createAccountForExistingCustomerAlternative } from '@/stores/accountManagementService'
import { ResponseError } from 'account-api'
import type { OrchestrateAccountsResponse } from 'account-api'

dayjs.extend(customParseFormat)

const props = defineProps<{
  initialVsnr: string
  initialEmail?: string
}>()

const emit = defineEmits<{
  'account:created': [response?: OrchestrateAccountsResponse]
  goToPostcodeAndBirthdate: any
}>()

const vsnrValue = ref(props.initialVsnr)
const emailValue = ref(props.initialEmail)
const postcodeValue = ref('')
const birthdateValue = ref('')
const vsnrTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const emailTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const postcodeTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const birthdateTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const pending = ref(false)
const showErrorResponse = ref(false)
const showInfoModal = ref(false)
const modalRef = ref(null)
const isTenantDA = ref(true)

onMounted(() => {
  isTenantDA.value = isDa()
})

const onSubmit = (): void => {
  validateInput()

  if (
    emailTextfieldStatus.value !== InputStatus.VALID ||
    vsnrTextfieldStatus.value !== InputStatus.VALID ||
    postcodeTextfieldStatus.value !== InputStatus.VALID ||
    birthdateTextfieldStatus.value !== InputStatus.VALID
  ) {
    return
  }

  sendCreateAccountRequest()
}

function updateVsnrValue(val: string) {
  vsnrValue.value = val.trim()
}

function updateEmailValue(val: string) {
  emailValue.value = val.trim()
}

function updatePostcodeValue(val: string) {
  postcodeValue.value = val.trim()
}

function openInfoModal() {
  showInfoModal.value = true
}

function closeInfoModal() {
  showInfoModal.value = false
}

const validateInput = (): void => {
  vsnrTextfieldStatus.value = checkIfVsnrInputIsValid(vsnrValue.value)
  emailTextfieldStatus.value = checkIfEmailInputIsValid(emailValue.value)
  postcodeTextfieldStatus.value = checkIfPostcodeInputIsValid(postcodeValue.value)
  birthdateTextfieldStatus.value = checkIfBirthdayInputIsValid(birthdateValue.value)
}

const sendCreateAccountRequest = async (): Promise<void> => {
  pending.value = true
  const birthdateFormatted = dayjs(birthdateValue.value, 'DD-MM-YYYY').format(
    'YYYY-MM-DD'
  )

  try {
    const response = await createAccountForExistingCustomerAlternative(
      escapeVSNR(vsnrValue.value),
      emailValue.value ?? '',
      postcodeValue.value,
      birthdateFormatted
    )
    // Erfolg
    emit('account:created', response)
    showErrorResponse.value = false
  } catch (e) {
    if (e instanceof ResponseError) {
      const status = e.response.status
      if (is5xxError(status)) {
        navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
      } else {
        const userExists = await isError400UserExisting(e.response)
        if (userExists) {
          navigate(globalThis.ENV_CONFIG?.REDIRECTS.USER_EXISTS_PAGE)
        } else {
          showErrorResponse.value = true
        }
      }
    } else {
      // Nicht-ResponseError
      navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
    }
  } finally {
    pending.value = false
  }
}

const notifyErrorDate = (): void => {
  birthdateTextfieldStatus.value = InputStatus.ERROR
}

const notifyInputDate = (): void => {
  birthdateTextfieldStatus.value = checkIfBirthdayInputIsValid(birthdateValue.value)
}

const notifyInputPostcode = (): void => {
  postcodeTextfieldStatus.value = checkIfPostcodeInputIsValid(postcodeValue.value)
}
</script>

<style lang="scss" scoped>
span.error-message {
  color: var(--input-color-error);
  font-size: 24px;
}

b.error-message {
  color: var(--input-color-error);
  padding-left: 5px;
}

.icon-vorteile {
  position: relative;
  color: var(--input-color-error);
  border: none;
  outline: none;
  margin-left: 6px;
  padding-inline: 0;
  top: 2px;
  font-size: var(--icon-font-size) !important;
  &:focus-visible::before {
    outline: 2px solid var(--primary-color-0) !important;
    outline-offset: 2px;
    border-radius: 2px;
  }
}

.hereButton {
  cursor: pointer;
  font-family: var(--f-primary-bold), serif !important;
  border: none;
  outline: none;
  padding-left: 0 !important;
  padding-right: 0;
}

ul {
  list-style-type: disc;
  padding-bottom: 30px !important;
  list-style-position: outside !important;
  padding-left: 20px !important;
}
li {
  text-indent: -13px;
  padding-left: 20px;
  padding-top: 5px;
}
li::before {
  content: '•';
  margin-right: 8px;
  color: var(--primary-color-0);
}

.modal-text {
  padding-right: 20px !important;
}

.modal {
  max-width: 500px;
  padding-right: 100px;
}

#registration-button {
  width: 100%;
  margin-bottom: 10px;
}

.additionalHelp {
  margin-bottom: 0 !important;
}

.no-wrap {
  white-space: nowrap;
}
</style>
